// colors https://colors.design4u.jp/app/index.html?hex0=%23dc2171&stp0=3&str0=1.3&ptn0=0&adj0=0&vvd0=13&sft0=0&pos0=4&vps0=5&hex1=%233e69b3&stp1=3&str1=1.0&ptn1=1&adj1=0&vvd1=0&sft1=0&pos1=2&vps1=5&

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMediaContext } from 'context/MediaContext'

import { Alert, Autocomplete, Avatar, Box, Card, CardContent, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { Bar, Pie } from 'react-chartjs-2'
import { Chart, ChartData, DefaultDataPoint } from 'chart.js'

import { DeckType } from 'model/DeckType'
import { Season } from 'model/Season'
import { Statistics } from 'model/Statistics'

import * as userRepository from 'repository/UserRepository'
import * as seasonRepository from 'repository/SeasonRepository'
import * as statisticsRepository from 'repository/StatisticsRepository'

import { getString } from 'script/dateutil'
import { useAuthContext } from 'context/AuthContext'
import { User } from 'model/User'

import quizimg from '../../asset/quiz.png'

import chartjsPluginDatalabels from 'chartjs-plugin-datalabels'
Chart.register(chartjsPluginDatalabels)

export default function Analytics() {
  const { isPc } = useMediaContext()
  const navigate = useNavigate()
  const { auth } = useAuthContext()

  const [user, setUser] = useState<User | null>(null)
  useEffect(() => {
    if (!auth) return
    userRepository.findFromCache(auth.uid).then(it => {
        setUser(it)
    })
  }, [auth])

  const [seasons, setSeasons] = useState<Season[]>([])
  const [season, setSeason] = useState<Season | null>(null)
  useEffect(() => {
    if (!user) return

    seasonRepository
      .listFromCache(undefined, [{ key: 'end_time', condition: 'desc' }])
      .then(it => {
        const target = it.filter(_it => _it.start_time <= new Date().getTime())
        setSeasons(target)
        setSeason(target[0])
      })
  }, [user])

  const [statistics, setStatistics] = useState<Statistics | null>(null)
  useEffect(() => {
    if (!season) return

    statisticsRepository
      .listFromCache(
        [{ key: 'season.id', condition: '==', value: season.id }],
        [{ key: 'update_time', condition: 'desc' }],
        1
      )
      .then(it => {
        if (!it || it.length === 0) {
          setStatistics(null)
        } else {
          const source = it[0].statistics.slice()
          it[0].statistics =
            it[0].statistics.filter(it => it.deck_type.main_theme.name !== 'その他')
          it[0].statistics.sort((a, b) => b.use_rate - a.use_rate)
          it[0].statistics.push(source.find(it => it.deck_type.main_theme.name === 'その他')!)
          setStatistics(it[0])
        }
      })
  }, [season])

  const [useRateData, setUseRateData] = useState<ChartData<"pie", DefaultDataPoint<"pie">, unknown> | null>(null)
  useEffect(() => {
    if (!statistics) {
      setUseRateData(null)
      return
    }

    const target = statistics.statistics.filter(it => {
      return it.deck_type.main_theme.name !== 'その他'
    }).slice(0, isPc ? 10 : 5)

    const labels = target.map(it => {
      if (user!.is_subscriber) {
        return getDeckTypeName(it.deck_type)
      } else {
        return '???'
      }
    }) 
    labels.push('ほか')

    const datas = target.map(it => it.use_rate)
    datas.push(100 - datas.reduce((s, e) => s + e))

    setUseRateData({
      labels: labels,
      datasets: [{
        data: datas,
        backgroundColor: [
          '#001f54',
          '#274581',
          '#5a70b1',
          '#8b9ee3',
          '#bb3877',
          '#f16ba6',
          '#ff9dd7',
          '#beb6cb',
          '#776f83',
          '#363041',
          'gray'
        ],
        borderColor: '#fff',
        borderWidth: 1,
      }]
    })
  }, [statistics])

  const [winRateData, setWinRateData] = useState<ChartData<"bar", DefaultDataPoint<"bar">, unknown> | null>(null)
  useEffect(() => {
    if (!statistics) {
      setUseRateData(null)
      return
    }
    const _stat = statistics.statistics.slice()
    _stat.sort((a, b) => b.win_rate - a.win_rate)
    const target = _stat.filter(it => {
      return it.deck_type.main_theme.name !== 'その他'
    }).slice(0, isPc ? 10 : 5)

    const labels = target.map(it => {
      if (user!.is_subscriber) {
        return getDeckTypeName(it.deck_type)
      } else {
        return '???'
      }
    })
    const datas = target.map(it => it.win_rate)

    setWinRateData({
      labels: labels,
      datasets: [{
        label: '',
        data: datas,
        backgroundColor: [
          '#001f54',
          '#274581',
          '#5a70b1',
          '#8b9ee3',
          '#bb3877',
          '#f16ba6',
          '#ff9dd7',
          '#beb6cb',
          '#776f83',
          '#363041',
        ]
      }]
    })
  }, [statistics])

  function getUpdateDate(): string {
    if (!statistics) return ''
    const datestr = getString(new Date(statistics.update_time))
    return `集計日時: ${datestr.slice(5, 16)}`
  }

  function getDeckTypeName(deckType: DeckType): string {
    if (!user?.is_subscriber) return '???'
    if (!deckType) return ''
    const mainThemeName = deckType.main_theme.name
    const subThemeName = deckType.sub_theme?.name
    let name = mainThemeName
    if (subThemeName) name += ` + ${subThemeName}`
    return name
  }

  return (
    <>
      {!user?.is_subscriber &&
        <Alert
          severity='info'
          variant='outlined'
          sx={{ backgroundColor: 'background.default', mb: 3 }}
        >
          <Typography variant='body2'>
            <Link onClick={() => navigate('/subscription')}>サブスクリプション</Link>
            に登録頂くと機能が開放されます。
          </Typography>
        </Alert>
      }
      <Card variant='outlined' sx={{
        px: isPc ? 2 : 0,
      }}>
        <CardContent>
          <Typography  sx={{mb: 1}}>R4MD Analytics</Typography>
          <Autocomplete
            options={seasons!}
            getOptionLabel={op => op.name}
            isOptionEqualToValue={(op, val) => op.id === val.id}
            renderInput={(params) =>
              <TextField
                {...params}
                variant='standard'
                required
              />}
            value={season}
            onChange={(e, v) => setSeason(v)}
          />
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{mt: 1}}
          >
            {getUpdateDate()}
          </Typography>

          <Typography color='text.secondary' sx={{mt: 2}}>使用率Top{isPc ? '10' : '5'}</Typography>
          <Card>
            <CardContent>
              {useRateData &&
                <Pie data={useRateData} options={chartOptions} />
              }
            </CardContent>
          </Card>

          <Typography color='text.secondary' sx={{mt: 3}}>勝率Top{isPc ? '10' : '5'}</Typography>
          <Card>
            <CardContent>
              {winRateData &&
                <Bar data={winRateData} options={barChartOptions} />
              }
            </CardContent>
          </Card>

          <Typography color='text.secondary' sx={{mt: 3}}>一覧</Typography>
          <Card sx={{px: 1, pb: 2}}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>デッキ</TableCell>
                    <TableCell>使用率</TableCell>
                    <TableCell>勝率</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statistics?.statistics.map(it => (
                    <React.Fragment key={it.deck_type.id}>
                      <TableRow>
                        <TableCell>
                          <Box display='flex' flexDirection='row' alignItems='center'>
                            <Avatar
                              src={user?.is_subscriber ? it.deck_type.thumbnail_url : quizimg}
                              variant='rounded'
                              sx={{mr: 0.5}}
                            />
                            <Link
                              sx={{ textDecoration: 'none', cursor: 'pointer' }}
                              onClick={() => navigate('/analyticsdetail', {
                                state: {
                                  is_subscriber: user?.is_subscriber,
                                  statistics: statistics,
                                  target: it
                                }
                              })}
                            >
                              {getDeckTypeName(it.deck_type)}
                            </Link>
                          </Box>
                        </TableCell>
                        <TableCell>{it.use_rate}%</TableCell>
                        <TableCell>{it.win_rate}%</TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

        </CardContent>
      </Card>
    </>
  )
}

const chartOptions = {
  plugins: {
    datalabels: {
      color: '#fff',
      formatter: (value: number, ctx: any) => {
        return ctx.chart.data.labels[ctx.dataIndex]
      }
    },
    legend: {display: false}
  }
}

const barChartOptions =  {
  indexAxis: 'y' as const,
  scales: {
    xAxis: {
      min: 50,
      max: 70,
    }
  },
  plugins: {
    datalabels: {
      display: false
    },
    legend: {
      display: false
    }
  }
}